import {useSelector} from 'react-redux';

import {useGetBranchQuery} from '@dms/api/core';
import {selectActiveBranchId} from '@dms/api/features';

/**
 * Options for configuring branch data fetching behavior
 */
export interface UseGetCurrentBranchOptions {
  /**
   * Enable refetching when component mounts or query arguments change.
   *
   * @default false
   *
   * Use this option when:
   * - Component needs the most up-to-date branch data on mount
   * - Query arguments (e.g., branchId) may change during component lifecycle
   *
   * Avoid using when:
   * - Component is mounted/unmounted frequently (performance impact)
   * - Data changes are handled via RTK Query cache invalidation
   * - Data is relatively static (e.g., display-only views)
   */
  refetchOnMountOrArgChange?: boolean;

  /**
   * Enable refetching when the browser window/tab gains focus.
   *
   * @default false
   *
   * Use this option when:
   * - Component displays data that may be modified in other tabs
   * - Data freshness on tab return is critical for correctness
   * - Example: Invoice creation page with bank account selection
   *
   * Avoid using when:
   * - Component is used in many places across the app (performance impact)
   * - Data changes are handled via RTK Query cache invalidation
   * - Data is relatively static (e.g., display-only views)
   */
  refetchOnFocus?: boolean;

  /**
   * Enable refetching when the network connection is re-established after being lost.
   *
   * @default false
   *
   * Use this option when:
   * - Component needs to recover from temporary network issues
   * - Data freshness after reconnect is important for user experience
   *
   * Avoid using when:
   * - Component is used in many places across the app (performance impact)
   * - Data changes are handled via RTK Query cache invalidation
   * - Data is relatively static (e.g., display-only views)
   */
  refetchOnReconnect?: boolean;

  /**
   * Enable polling to periodically refetch data in the background.
   *
   * @default undefined (no polling)
   *
   * Provide interval in milliseconds (e.g., 60000 for 1 minute).
   * Use sparingly as it increases server load and network usage.
   */
  pollingInterval?: number;
}

export const useGetCurrentBranch = (options?: UseGetCurrentBranchOptions) => {
  const activeBranchId = useSelector(selectActiveBranchId);

  const activeBranch = useGetBranchQuery(
    {branchId: activeBranchId},
    {
      skip: !activeBranchId,
      refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? false,
      refetchOnFocus: options?.refetchOnFocus ?? false,
      refetchOnReconnect: options?.refetchOnReconnect ?? false,
      pollingInterval: options?.pollingInterval,
    }
  );

  return {
    activeBranchId,
    isSynced: Boolean(activeBranchId) && activeBranch?.data?.id === activeBranchId,
    ...activeBranch,
  };
};
